import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  ArrowRight, 
  ArrowUpRight, 
  ArrowLeft, 
  Download, 
  Check, 
  Copy, 
  MessageCircle, 
  Phone, 
  Mail, 
  Calendar, 
  Send,
  Loader2
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { easings, durations } from './motionTokens';

/**
 * Prime Business Zone — Function-Specific Button System
 * Implements purpose-driven, consistent motion across button families.
 */
export const FunctionButton = ({
  variant = 'internal-cta', // 'explore-availability' | 'internal-cta' | 'external-link' | 'download-brochure' | 'copy-address' | 'whatsapp' | 'phone' | 'email' | 'form-submit' | 'back-button'
  to,
  href,
  onClick,
  children,
  className = '',
  icon: CustomIcon,
  loading = false,
  success = false,
  copiedText = 'Copied',
  type = 'button',
  disabled = false,
  ...props
}) => {
  const [isCopied, setIsCopied] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  // Handle copy to clipboard for copy-address variant
  const handleCopy = async (e) => {
    if (onClick) onClick(e);
    if (variant === 'copy-address' && props.copyValue) {
      try {
        await navigator.clipboard.writeText(props.copyValue);
        setIsCopied(true);
        setTimeout(() => setIsCopied(false), 1600);
      } catch {
        // Fallback
      }
    }
  };

  // Base Button Press Animation
  const pressVariants = {
    rest: { scale: 1 },
    hover: { scale: 1.01 },
    tap: { scale: 0.97 }
  };

  // 1. EXPLORE AVAILABILITY CTA (Muted-gold, initial sheen pass, magnetic response)
  if (variant === 'explore-availability') {
    const content = (
      <motion.button
        type={type}
        disabled={disabled || loading}
        onClick={onClick}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`relative overflow-hidden inline-flex items-center justify-center gap-2.5 px-6 py-3.5 rounded-xl bg-gradient-to-r from-[#B99A5B] to-[#D4B678] text-[#070C12] font-bold text-xs uppercase tracking-wider shadow-[0_4px_20px_rgba(185,154,91,0.3)] transition-all cursor-pointer ${className}`}
        {...props}
      >
        {/* Slow sheen passes across surface */}
        <motion.div
          animate={{ x: ['-100%', '200%'] }}
          transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
          className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 pointer-events-none"
        />

        <span className="relative z-10">{children}</span>

        <motion.div
          animate={isHovered ? { x: 3 } : { x: 0 }}
          transition={{ duration: durations.hover, ease: easings.entrance }}
          className="relative z-10"
        >
          {CustomIcon ? <CustomIcon className="w-4 h-4" /> : <ArrowRight className="w-4 h-4" />}
        </motion.div>
      </motion.button>
    );

    return to ? <Link to={to} className="inline-block">{content}</Link> : content;
  }

  // 2. STANDARD INTERNAL NAVIGATION CTA (Arrow translates 4-6px, underline draws left to right)
  if (variant === 'internal-cta') {
    const content = (
      <motion.button
        type={type}
        disabled={disabled || loading}
        onClick={onClick}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`group relative inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#B99A5B] hover:text-[#F2EEE6] transition-colors cursor-pointer py-1 ${className}`}
        {...props}
      >
        <span className="relative">
          {children}
          {/* Underline draws left to right */}
          <motion.span
            className="absolute left-0 bottom-0 w-full h-[1px] bg-[#B99A5B] origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: isHovered ? 1 : 0 }}
            transition={{ duration: durations.hover, ease: easings.entrance }}
          />
        </span>

        <motion.div
          animate={isHovered ? { x: 5 } : { x: 0 }}
          transition={{ duration: durations.hover, ease: easings.entrance }}
        >
          {CustomIcon ? <CustomIcon className="w-3.5 h-3.5" /> : <ArrowRight className="w-3.5 h-3.5" />}
        </motion.div>
      </motion.button>
    );

    return to ? <Link to={to} className="inline-block">{content}</Link> : content;
  }

  // 3. EXTERNAL LINK (North-east arrow moves diagonally 3px)
  if (variant === 'external-link') {
    return (
      <motion.a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#B99A5B] hover:text-[#F2EEE6] transition-colors ${className}`}
        {...props}
      >
        <span>{children}</span>
        <motion.div
          animate={isHovered ? { x: 2.5, y: -2.5 } : { x: 0, y: 0 }}
          transition={{ duration: durations.hover, ease: easings.entrance }}
        >
          <ArrowUpRight className="w-3.5 h-3.5" />
        </motion.div>
      </motion.a>
    );
  }

  // 4. DOWNLOAD BROCHURE (Download arrow drops into tray, morphs to checkmark)
  if (variant === 'download-brochure') {
    return (
      <motion.button
        type={type}
        disabled={disabled || loading}
        onClick={onClick}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl border border-[#B99A5B]/40 bg-[#0B1118]/80 text-[#B99A5B] hover:border-[#B99A5B] hover:bg-[#B99A5B]/10 hover:text-[#F2EEE6] text-xs font-bold uppercase tracking-wider transition-all cursor-pointer ${className}`}
        {...props}
      >
        {loading ? (
          <Loader2 className="w-4 h-4 animate-spin text-[#B99A5B]" />
        ) : success ? (
          <Check className="w-4 h-4 text-emerald-400" />
        ) : (
          <motion.div
            animate={isHovered ? { y: 2 } : { y: 0 }}
            transition={{ duration: durations.hover, ease: easings.entrance }}
          >
            <Download className="w-4 h-4" />
          </motion.div>
        )}
        <span>{success ? 'Downloaded' : loading ? 'Preparing...' : children}</span>
      </motion.button>
    );
  }

  // 5. COPY ADDRESS / DATA (Clipboard morphs to checkmark with aria-live feedback)
  if (variant === 'copy-address') {
    return (
      <motion.button
        type="button"
        onClick={handleCopy}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        className={`inline-flex items-center gap-2 text-xs font-mono text-[#B99A5B] hover:text-[#F2EEE6] transition-colors cursor-pointer ${className}`}
        aria-live="polite"
        {...props}
      >
        {isCopied ? (
          <>
            <Check className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-emerald-400">{copiedText}</span>
          </>
        ) : (
          <>
            <Copy className="w-3.5 h-3.5" />
            <span>{children}</span>
          </>
        )}
      </motion.button>
    );
  }

  // 6. WHATSAPP BUTTON (Restrained ripple & label expansion)
  if (variant === 'whatsapp') {
    return (
      <motion.a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#25D366] text-white font-bold text-xs shadow-lg transition-all ${className}`}
        {...props}
      >
        <MessageCircle className="w-4 h-4" />
        <span>{children || 'WhatsApp'}</span>
      </motion.a>
    );
  }

  // 7. PHONE BUTTON (Handset rotates ~6deg on hover)
  if (variant === 'phone') {
    return (
      <motion.a
        href={href}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#F2EEE6] hover:text-[#B99A5B] transition-colors ${className}`}
        {...props}
      >
        <motion.div
          animate={isHovered ? { rotate: [0, -8, 8, -4, 0] } : { rotate: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Phone className="w-4 h-4 text-[#B99A5B]" />
        </motion.div>
        <span>{children}</span>
      </motion.a>
    );
  }

  // 8. EMAIL BUTTON (Envelope icon lift)
  if (variant === 'email') {
    return (
      <motion.a
        href={href}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#F2EEE6] hover:text-[#B99A5B] transition-colors ${className}`}
        {...props}
      >
        <motion.div
          animate={isHovered ? { y: -2 } : { y: 0 }}
          transition={{ duration: durations.hover }}
        >
          <Mail className="w-4 h-4 text-[#B99A5B]" />
        </motion.div>
        <span>{children}</span>
      </motion.a>
    );
  }

  // 9. FORM SUBMIT BUTTON (Paper-plane animation on submit)
  if (variant === 'form-submit') {
    return (
      <motion.button
        type="submit"
        disabled={disabled || loading}
        variants={pressVariants}
        initial="rest"
        whileHover={!loading && !disabled ? "hover" : undefined}
        whileTap={!loading && !disabled ? "tap" : undefined}
        className={`relative inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-[#B99A5B] text-[#070C12] font-bold text-xs uppercase tracking-widest shadow-[0_4px_20px_rgba(185,154,91,0.25)] transition-all cursor-pointer ${className}`}
        {...props}
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin text-[#070C12]" />
            <span>Processing...</span>
          </>
        ) : success ? (
          <>
            <Check className="w-4 h-4 text-[#070C12]" />
            <span>Submitted Successfully</span>
          </>
        ) : (
          <>
            <span>{children || 'Submit'}</span>
            <Send className="w-4 h-4 text-[#070C12]" />
          </>
        )}
      </motion.button>
    );
  }

  // 10. BACK BUTTON (Arrow moves left before navigation)
  if (variant === 'back-button') {
    const content = (
      <motion.button
        type={type}
        onClick={onClick}
        variants={pressVariants}
        initial="rest"
        whileHover="hover"
        whileTap="tap"
        onHoverStart={() => setIsHovered(true)}
        onHoverEnd={() => setIsHovered(false)}
        className={`inline-flex items-center gap-2 text-xs font-mono font-bold uppercase tracking-wider text-[#B99A5B] hover:text-[#F2EEE6] transition-colors cursor-pointer ${className}`}
        {...props}
      >
        <motion.div
          animate={isHovered ? { x: -4 } : { x: 0 }}
          transition={{ duration: durations.hover, ease: easings.entrance }}
        >
          <ArrowLeft className="w-4 h-4" />
        </motion.div>
        <span>{children || 'Back'}</span>
      </motion.button>
    );

    return to ? <Link to={to} className="inline-block">{content}</Link> : content;
  }

  // Default Fallback
  return (
    <motion.button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      variants={pressVariants}
      initial="rest"
      whileHover="hover"
      whileTap="tap"
      className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#B99A5B] text-[#070C12] font-bold text-xs uppercase tracking-wider ${className}`}
      {...props}
    >
      {children}
    </motion.button>
  );
};

export default FunctionButton;
